import { CarPerf, CarState, Input, carPerf, stepCar } from './car';
import { hashNumbers } from './fmath';
import { LapEvent, LapState, freshLap, gridCar, respawnCar, updateLap, updateStreak } from './laps';
import { Mods, applyMods } from './mods';
import { TeamSpec } from './teams';
import { Track } from './track';
import { CRASH_RESPAWN_TICKS } from './tuning';

export type { LapState } from './laps';

export type SimEvent =
  | LapEvent
  | { type: 'crash' }
  | { type: 'respawn' }
  | { type: 'streak'; bonusMph: number };

/**
 * Single-car hot-lap world (Time Trial). Deterministic: same initial state
 * + same inputs ⇒ identical states on every JS engine.
 */
export class World {
  readonly track: Track;
  team: TeamSpec;
  perf: CarPerf;
  car: CarState;
  lap: LapState;
  tick = 0;
  events: SimEvent[] = [];
  /** Daily conditions/twist (null = standard car). */
  readonly mods: Mods | null;

  constructor(track: Track, team: TeamSpec, startSlot = 0, mods: Mods | null = null) {
    this.track = track;
    this.team = team;
    this.mods = mods;
    this.perf = applyMods(carPerf(team), mods);
    this.car = gridCar(track, startSlot);
    this.lap = freshLap();
  }

  static freshLap(): LapState {
    return freshLap();
  }

  gridCar(slot: number): CarState {
    return gridCar(this.track, slot);
  }

  setTeam(team: TeamSpec): void {
    this.team = team;
    this.car.bonusMph = 0;
    this.perf = applyMods(carPerf(team), this.mods);
  }

  step(input: Input): void {
    this.events.length = 0;
    const car = this.car;
    const x0 = car.x;
    const y0 = car.y;

    if (car.crashed) {
      stepCar(car, this.perf, input, this.track);
      if (car.crashTicks >= CRASH_RESPAWN_TICKS) {
        this.car = respawnCar(this.track, this.lap.lastGate, car.bonusMph);
        this.events.push({ type: 'respawn' });
      }
      this.tick++;
      return;
    }

    const res = stepCar(car, this.perf, input, this.track);
    if (res.crashed) {
      this.events.push({ type: 'crash' });
      this.lap.valid = false;
      this.lap.streakDist = 0;
    }

    updateLap(this.track, this.lap, this.tick, x0, y0, car.x, car.y, this.events as LapEvent[]);
    if (updateStreak(this.track, car, this.lap, input)) {
      this.perf = applyMods(carPerf(this.team, car.bonusMph), this.mods);
      this.events.push({ type: 'streak', bonusMph: car.bonusMph });
    }
    this.tick++;
  }

  /** Put the car back on the grid and forget the current lap (not records). */
  restart(): void {
    const best = this.lap.bestLapTicks;
    this.car = gridCar(this.track, 0);
    this.lap = freshLap();
    this.lap.bestLapTicks = best;
    this.perf = applyMods(carPerf(this.team), this.mods);
  }

  /** Lap time so far, in ticks (0 before the first crossing). */
  currentLapTicks(): number {
    return this.lap.startTick < 0 ? 0 : this.tick - this.lap.startTick;
  }

  hash(): number {
    const c = this.car;
    return hashNumbers([this.tick, c.x, c.y, c.vx, c.vy, c.h, c.bonusMph, c.crashed ? 1 : 0, this.lap.startTick, this.lap.nextGate]);
  }
}
